import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, Pencil, ShieldCheck, UserCog } from 'lucide-react';
import { PERMISSION_ROLES, resolveAccessibleKRs } from '../../lib/employeeAdmin';

function Field({ label, value, mono }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`text-sm text-gray-800 truncate ${mono ? 'font-mono text-xs' : ''}`}>
        {value || <span className="italic text-gray-400">Not set</span>}
      </p>
    </div>
  );
}

function Warning({ children }) {
  return (
    <div className="flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
      <AlertTriangle size={13} className="mt-0.5 flex-shrink-0 text-amber-600" />
      <span>{children}</span>
    </div>
  );
}

/**
 * The manager, editable in place.
 *
 * Until now this was the one repair the panel could diagnose but not perform: it
 * warns that a missing `managerId` blocks the manager from opening an evaluation,
 * while the only writer of that field was employee creation. Setting it here
 * fixes `managerId`, `managerEmail` and both sides of `directReports` in one go.
 *
 * Free-text input backed by a datalist rather than a select, matching
 * AddEmployeeModal — the roster is long enough that typing beats scrolling, and
 * an unmatched address is caught on save rather than silently stored.
 */
function ManagerField({ user, users, manager, onUpdateManager }) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(user.managerEmail || '');
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  /* The roster is a live snapshot, so a change made elsewhere — or the one just
     saved here — has to land in the draft rather than leaving a stale email. */
  useEffect(() => {
    if (!isEditing) setDraft(user.managerEmail || '');
  }, [user.managerEmail, isEditing]);

  const candidateEmails = useMemo(() => (
    (users || [])
      .filter(u => u.email && u.id !== user.id)
      .map(u => u.email)
      .sort((a, b) => a.localeCompare(b))
  ), [users, user.id]);

  const resolved = useMemo(() => {
    const wanted = draft.trim().toLowerCase();
    if (!wanted) return null;
    return (users || []).find(u => (u.email || '').toLowerCase() === wanted) || null;
  }, [draft, users]);

  const unresolved = !!draft.trim() && !resolved;

  const cancel = () => {
    setDraft(user.managerEmail || '');
    setError('');
    setIsEditing(false);
  };

  const save = async () => {
    setIsBusy(true);
    setError('');
    try {
      const result = await onUpdateManager(user.id, draft.trim());
      if (result?.ok) {
        setIsEditing(false);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError(result?.message || 'The manager could not be saved.');
      }
    } catch (unexpected) {
      console.error('Saving the manager failed:', unexpected);
      setError('The manager could not be saved. Please try again.');
    } finally {
      setIsBusy(false);
    }
  };

  if (!onUpdateManager) {
    return <Field label="Manager" value={user.managerEmail} />;
  }

  if (!isEditing) {
    return (
      <div className="min-w-0">
        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Manager</p>
        <div className="flex items-center gap-1.5 min-w-0">
          <p className="text-sm text-gray-800 truncate">
            {manager?.name || user.managerEmail || <span className="italic text-gray-400">Not set</span>}
          </p>
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            title="Change their manager"
            className="p-0.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 flex-shrink-0"
          >
            <Pencil size={11} />
          </button>
          {saved && <Check size={12} className="text-emerald-600 flex-shrink-0" />}
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0 col-span-2 sm:col-span-3">
      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1.5">
        <UserCog size={11} className="text-gray-400" />
        Manager
      </p>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <input
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          list={`apex-manager-options-${user.id}`}
          placeholder="manager@ruskmedia.com — leave blank for none"
          disabled={isBusy}
          className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-gray-50"
        />
        <datalist id={`apex-manager-options-${user.id}`}>
          {candidateEmails.map(email => <option key={email} value={email} />)}
        </datalist>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={save}
            disabled={isBusy || unresolved}
            className="px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isBusy ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={cancel}
            disabled={isBusy}
            className="px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>

      {resolved && (
        <p className="text-[11px] text-emerald-700 mt-1.5">
          {resolved.name || resolved.email} will be able to review this person&rsquo;s evaluations.
        </p>
      )}
      {unresolved && (
        <p className="flex items-start gap-1.5 text-[11px] text-amber-800 mt-1.5">
          <AlertTriangle size={12} className="mt-0.5 flex-shrink-0 text-amber-600" />
          Nobody in the database has that address. Pick one from the list, or clear the box to
          remove their manager.
        </p>
      )}
      {!draft.trim() && (
        <p className="text-[11px] text-gray-500 mt-1.5">
          Saving an empty box removes their manager — nobody will be able to review their
          evaluations until a new one is set.
        </p>
      )}
      {error && (
        <p className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5 mt-1.5">
          {error}
        </p>
      )}
      <p className="text-[11px] text-gray-400 mt-1.5">
        Manager changes are read once at sign-in, so this person and their managers need to reload
        before it takes effect for them.
      </p>
    </div>
  );
}

/**
 * Expanded row: the profile as stored, the FY26-27 KR access it grants, and any
 * data problems worth an admin's attention.
 *
 * The warnings are the point of this panel. All of them are derived from data
 * already in memory, and each one describes a way the app quietly stops working
 * for that person — most often an evaluation their manager cannot open.
 */
export default function EmployeeDetailPanel({ user, users, teams, krCodeIndex, onUpdateManager }) {
  const uid = user.id;
  const manager = user.managerEmail
    ? users.find(u => (u.email || '').toLowerCase() === user.managerEmail.toLowerCase())
    : null;
  const managerName = manager?.name || manager?.email || null;

  const krs = resolveAccessibleKRs(user.accessibleKRs, krCodeIndex);
  const reports = users.filter(u => (u.managerEmail || '').toLowerCase() === (user.email || '').toLowerCase());
  const isPermissionRole = PERMISSION_ROLES.includes(user.role);

  // firestore.rules matches a manager on managerId, and EvaluationsView gates
  // manager mode on the manager's directReports — so either gap silently blocks
  // a real review.
  const missingManagerId = !!user.managerEmail && !user.managerId;
  const danglingManager = !!user.managerEmail && !manager;
  const notInManagerReports = !!manager
    && Array.isArray(manager.directReports) && manager.directReports.length > 0
    && !manager.directReports.includes(uid);

  return (
    <div className="px-4 pb-4 pt-3 bg-gray-50/60 border-t border-gray-100">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 mb-3">
        <Field label="Email" value={user.email} />
        <Field label="Role" value={user.role} />
        <Field label="Team" value={teams[user.teamId]?.name || user.team} />
        <ManagerField user={user} users={users} manager={manager} onUpdateManager={onUpdateManager} />
        <Field label="Direct reports" value={String((user.directReports || []).length)} />
        <Field label="User ID" value={uid} mono />
      </div>

      {(missingManagerId || danglingManager || notInManagerReports || isPermissionRole) && (
        <div className="space-y-1.5 mb-3">
          {isPermissionRole && (
            <div className="flex items-start gap-2 text-[11px] text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-2.5 py-1.5">
              <ShieldCheck size={13} className="mt-0.5 flex-shrink-0 text-blue-600" />
              <span><span className="font-semibold">{user.role}</span> is a permission level, not a job title — this person has elevated access.</span>
            </div>
          )}
          {danglingManager && (
            <Warning>
              Their manager <span className="font-semibold">{user.managerEmail}</span> is not in the database.
              Nobody can review this person&rsquo;s evaluations until a current manager is set.
            </Warning>
          )}
          {missingManagerId && !danglingManager && (
            <Warning>
              <span className="font-semibold">managerId is missing.</span> Firestore rules match a manager by ID,
              so {managerName || 'their manager'} cannot open their evaluations even though the email is set.
            </Warning>
          )}
          {notInManagerReports && (
            <Warning>
              Not listed in {managerName || 'their manager'}&rsquo;s direct reports, so they will not appear
              in that manager&rsquo;s evaluation list.
            </Warning>
          )}
        </div>
      )}

      <div>
        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">
          FY 26-27 KR access ({krs.length})
        </p>
        {krs.length === 0 ? (
          <p className="text-xs text-gray-400 italic">No FY 26-27 KR access.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {krs.map(kr => (
              <span
                key={kr.code}
                title={kr.unknown ? 'This KR no longer exists in FY 26-27' : `${kr.objectiveTitle} — ${kr.krText}`}
                className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded border max-w-full ${
                  kr.unknown
                    ? 'bg-gray-100 border-gray-200 text-gray-500'
                    : 'bg-white border-blue-200 text-gray-700'
                }`}
              >
                <span className="font-mono font-bold text-[10px] text-blue-700">{kr.code}</span>
                <span className="truncate max-w-[16rem]">
                  {kr.unknown ? 'Unknown KR (removed)' : (kr.krText || kr.objectiveTitle)}
                </span>
              </span>
            ))}
          </div>
        )}
      </div>

      {reports.length > 0 && (
        <p className="text-[11px] text-gray-500 mt-3">
          <span className="font-semibold text-gray-700">{reports.length}</span> {reports.length === 1 ? 'person reports' : 'people report'} to them
          {' '}({reports.slice(0, 3).map(r => r.name || r.email).join(', ')}{reports.length > 3 ? `, +${reports.length - 3} more` : ''}).
        </p>
      )}
    </div>
  );
}
